import { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";

import Header from "../../Common/header";
import ExplorerMapContainer from "./ExplorerMapContainer";
import ExplorerKeywordFilter from "./explorerKeywordFilter";

import { API } from "../../Common/lib/api";
import getAuthMode from "../../Common/getAuthMode";
import useDocumentTitle from "../../Common/useDocumentTitle";

export default function DataExplorer() {
  useDocumentTitle("Data Explorer");
  let history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [openList, setOpenList] = useState(false);
  const listRef = useRef();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filters, setFilters] = useState([]);
  const [keywordChoices, setKeywordChoices] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Will filter the projects to be displayed on the map when filters are added/removed.
   */
  useEffect(() => {
    if (filters.length === 0) {
      setFilteredProjects(projects);
      setSelectedProjects(projects);
    } else {
      let filteredProjects = [];

      projects.forEach((project) => {
        for (const filter of filters) {
          if (project.keywords.toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
            filteredProjects.push(project);
            break;
          }
        }
      });

      setFilteredProjects(filteredProjects);
      setSelectedProjects(filteredProjects);
    }
  }, [filters]);

  /**
   * Retrieves all projects from dynamoDB.
   */
  const fetchData = async () => {
    try {
      const results = await API.listProjects({ publishStatus: "published" });
      let projects = results.data.listProjects.items;
      setProjects(projects);
      setFilteredProjects(projects);
      setSelectedProjects(projects);

      let existingKeywords = [];

      projects.forEach((project) => {
        JSON.parse(project.keywords).forEach((keyword) => {
          existingKeywords.push(keyword);
        });
      });

      getKeywords(existingKeywords);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setIsLoading(false);
    }
  };

  /**
   * Retrieves all keywords that are in projects, excluding duplicates, and sets them as the keywordChoices state
   */
  const getKeywords = async (existingKeywords) => {
    try {
      const attributesQuery = await API.listAttributes({ category: "keyword" });
      let currentItems = [...attributesQuery.data.listAttributes.items];

      let items = [];
      currentItems.forEach((item) => {
        if (existingKeywords.indexOf(item.name) === -1 && !items.includes(item.name)) {
          items.push(item.name);
        }
      });

      setKeywordChoices(items);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Header />
      <div style={{ flex: 1 }} className="relative overflow-hidden">
        {isLoading ? (
          <div role="status" className="text-center mt-64">
            <svg
              aria-hidden="true"
              className="inline w-20 h-20 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          <>
            <ExplorerMapContainer
              setSelectedProjects={setSelectedProjects}
              projects={projects}
              setOpenList={setOpenList}
              filteredProjects={filteredProjects}
              listRef={listRef}
            />

            <ExplorerKeywordFilter
              keywordChoices={keywordChoices}
              setFilters={setFilters}
              filters={filters}
            />

            {/* Datasets section */}
            <div className="absolute bottom-0 right-0">
              <div
                className="bg-gray-50 border-gray-300 border-l border-t w-screen full-w lg:w-[28rem] lg:rounded-tl-lg shadow-md cursor-pointer dark:bg-gray-800 dark:border-gray-700"
                onClick={() => setOpenList(!openList)}
                data-cy="dataset-list"
              >
                <div className="flex justify-between items-center py-3 px-4">
                  <p className="text-base dark:text-white" data-cy="datasets">
                    {selectedProjects.length}/{filteredProjects.length} Datasets
                  </p>
                  <svg
                    className={`-mt-1 h-8 w-8 text-gray-400 transition ease-in-out duration-350 ${
                      openList ? "rotate-180" : "rotate-0"
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              <div
                className={`border-gray-300 border-l bg-gray-50 w-screen lg:w-[28rem] overflow-y-auto overflow-x-hidden transition-all ease-in-out duration-350 dark:bg-gray-800 dark:border-gray-700 ${
                  openList ? "h-[50vh] lg:h-[70vh] border-t" : "h-[0vh]"
                }`}
                ref={listRef}
                data-cy="projects-panel"
              >
                {selectedProjects.map((project) => {
                  return (
                    <div
                      key={project.id || project.PK || project.title}
                      className="max-w-full border-gray-300 border-b last:border-b-0 py-4 bg-gray-50 relative px-4 dark:bg-gray-800 dark:border-gray-700"
                      data-cy="project"
                    >
                      <a
                        href={"/metadata-display/" + project.id}
                        target="_blank"
                        className="max-w-full line-clamp-2 text-blue-600 font-semibold hover:underline dark:text-blue-400"
                        data-cy={project.id || project.PK}
                      >
                        {project.title}
                      </a>
                      <p className="max-w-full line-clamp-4 text-sm text-gray-500 font-medium py-1 dark:text-gray-400">{project.abstract}</p>
                      <div className="text-sm flex justify-between text-gray-600 pt-1">
                        <p className="basis-1/3 font-medium text-gray-600 dark:text-gray-300">Published</p>
                        <p className="basis-2/3 font-medium text-gray-600 dark:text-gray-300">Published By</p>
                      </div>
                      <div className="text-sm flex justify-between text-gray-500 dark:text-gray-400">
                        <p className="basis-1/3">
                          {project.publishedAt ? new Date(project.publishedAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }) : 'N/A'}
                        </p>
                        <p className="basis-2/3 truncate">
                          {
                            JSON.parse(project.stakeholders).find(
                              (stakeholder) => stakeholder.role === "Publisher"
                            ).name
                          }
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}